<template>
    <div class="blog-form-v3-container">
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div
                class="ppv2-flex ppv2-flex-row ppv2-items-center ppv2-justify-between"
                :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }"
            >
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ blogId ? $t('blog.edit_blog') : $t('blog.add_blog') }}
                </h1>

                <div v-if="blogId" class="ppv2-flex" :style="{ gap: normalizeSize(16, true) }">
                    <BaseButton
                        variant="secondary"
                        @click="saveDraft"
                        :loading="submitting && !form.is_publish"
                        :disabled="submitting"
                        customClass="!ppv2-border-black !ppv2-border-opacity-20 !ppv2-text-black !ppv2-bg-white !ppv2-min-w-[180px]"
                        :customHeight="48"
                    >
                        {{ $t('blog.save_draft') }}
                    </BaseButton>
                    <BaseButton
                        variant="primary"
                        @click="publish"
                        :loading="submitting && form.is_publish"
                        :disabled="submitting"
                        customClass="!ppv2-bg-gradient-to-r !ppv2-from-[#2E1E7B] !ppv2-to-[#7D2DBD] !ppv2-border-none !ppv2-min-w-[120px]"
                        :customHeight="48"
                    >
                        {{ $t('blog.publish') }}
                    </BaseButton>
                </div>
            </div>

            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Main Information -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('blog.main_information') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full ppv2-px-3">
                                    <CustomInput
                                        :label="$t('blog.columns.title')"
                                        v-model="form.title"
                                        :required="true"
                                        :placeholder="$t('blog.columns.title')"
                                        :errorText="errors.title ? errors.title[0] : ''"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mt-4">
                                    <BaseTextarea
                                        :label="$t('blog.content')"
                                        v-model="form.body"
                                        :placeholder="$t('blog.content')"
                                        :rows="10"
                                        :useEditor="true"
                                        :required="true"
                                        :errorText="errors.body ? errors.body[0] : ''"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mt-4">
                                    <div class="upload-container">
                                        <CustomInputUpload
                                            :label="$t('blog.image')"
                                            v-model="form.image"
                                            :required="false"
                                            :showImproveWithAI="false"
                                            accept="image/*"
                                            @input="onFileSelected"
                                            :errorText="errors.image"
                                            customClassLabel="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                </div>
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mt-4">
                                    <BaseTextarea
                                        :label="$t('blog.short_desc')"
                                        v-model="form.short_desc"
                                        :placeholder="$t('blog.short_desc')"
                                        :rows="3"
                                        :useEditor="false"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- SEO and Categories -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('blog.seo_optimization') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mt-6">
                                    <CustomInput
                                        label="SEO Title"
                                        v-model="form.seo"
                                        :maxlength="60"
                                        placeholder="SEO Title"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                    <div class="ppv2-flex ppv2-justify-end">
                                        <span class="ppv2-text-xs ppv2-text-gray-400 poppinsLight">
                                            {{ $t('website_settings.seo.char_max', { max: 60 }) }} -
                                            {{ (form.seo || '').length }}/60
                                        </span>
                                    </div>
                                </div>
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mt-4">
                                    <BaseTextarea
                                        label="Meta Description"
                                        v-model="form.meta_desc"
                                        :maxlength="160"
                                        placeholder="Meta Description"
                                        :rows="2"
                                        :useEditor="false"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                    <div class="ppv2-flex ppv2-justify-end">
                                        <span class="ppv2-text-xs ppv2-text-gray-400 poppinsLight">
                                            {{ $t('website_settings.seo.char_max', { max: 160 }) }} -
                                            {{ (form.meta_desc || '').length }}/160
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </v-card>

                        <!-- FAQ Block -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('blog.faq') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-p-6">
                                <div
                                    v-if="form.faqs.length === 0"
                                    class="ppv2-text-center ppv2-text-gray-500 poppinsLight"
                                    :style="{ paddingTop: normalizeSize(16, true), paddingBottom: normalizeSize(16, true) }"
                                >
                                    {{ $t('blog.no_faq') }}
                                </div>
                                <div v-else class="ppv2-space-y-6">
                                    <div
                                        v-for="(faq, index) in form.faqs"
                                        :key="index"
                                        class="ppv2-border ppv2-border-black ppv2-border-opacity-10 ppv2-rounded-[10px] ppv2-p-4"
                                    >
                                        <div class="ppv2-flex ppv2-justify-between ppv2-items-start ppv2-mb-4">
                                            <span class="poppinsSemiBold ppv2-text-black"
                                                >{{ $t('blog.faq_item') }} #{{ index + 1 }}</span
                                            >
                                            <div class="ppv2-flex ppv2-gap-2">
                                                <button
                                                    @click="moveUp(index)"
                                                    :disabled="index === 0"
                                                    class="ppv2-text-gray-400 hover:ppv2-text-black disabled:ppv2-opacity-30"
                                                >
                                                    <ChevronUp :size="18" />
                                                </button>
                                                <button
                                                    @click="moveDown(index)"
                                                    :disabled="index === form.faqs.length - 1"
                                                    class="ppv2-text-gray-400 hover:ppv2-text-black disabled:ppv2-opacity-30"
                                                >
                                                    <ChevronDown :size="18" />
                                                </button>
                                                <button
                                                    @click="removeFAQ(index)"
                                                    class="ppv2-text-red-500 hover:ppv2-text-red-700"
                                                >
                                                    <Trash2 :size="18" />
                                                </button>
                                            </div>
                                        </div>
                                        <div class="ppv2-space-y-4">
                                            <CustomInput
                                                :label="$t('blog.faq_question')"
                                                v-model="faq.question"
                                                :placeholder="$t('blog.faq_question')"
                                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                            />
                                            <BaseTextarea
                                                :label="$t('blog.faq_answer')"
                                                v-model="faq.answer"
                                                :placeholder="$t('blog.faq_answer')"
                                                :rows="3"
                                                :useEditor="false"
                                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-mt-2">
                                    <BaseButton
                                        id="button"
                                        type="button"
                                        variant="secondary"
                                        @click="addFAQ"
                                        :customFontSize="12"
                                    >
                                        <Plus :size="12" />
                                        <span>{{ $t('blog.btn_add_faq') }}</span>
                                    </BaseButton>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[35%] ppv2-space-y-6">
                        <div
                            class="ppv2-text-black ppv2-rounded-[10px] ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-p-6"
                        >
                            <!-- Status Toggle -->
                            <div class="ppv2-mb-8">
                                <BaseSwitch
                                    v-model="form.is_publish"
                                    :label="form.is_publish ? $t('blog.published') : $t('blog.unpublished')"
                                    :boxed="true"
                                    :variant="form.is_publish ? 'success' : 'gray'"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>

                            <div class="ppv2-space-y-6">
                                <!-- Publish Date -->
                                <div class="ppv2-w-full">
                                    <BaseDateInput
                                        :label="$t('blog.columns.publish_at')"
                                        v-model="form.publish_at"
                                        :defaultValue="form.publish_at"
                                        :customSizeLabel="15"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>

                                <!-- History Section -->
                                <div
                                    v-if="blogId && form.created_at"
                                    class="ppv2-pt-4 ppv2-border-t ppv2-border-black ppv2-border-opacity-10"
                                >
                                    <h3
                                        class="poppinsSemiBold ppv2-text-black ppv2-mb-2"
                                        :style="{ fontSize: normalizeFontSize(15) }"
                                    >
                                        {{ $t('product.history') }}
                                    </h3>
                                    <div class="ppv2-space-y-1">
                                        <p
                                            v-if="form.is_publish"
                                            class="poppinsLight ppv2-text-black mb-0"
                                            :style="{ fontSize: normalizeFontSize(13) }"
                                        >
                                            {{ $t('product.published_on') }} {{ formatDate(form.created_at) }}
                                            {{ $t('product.by') }} {{ form.blogger }}
                                        </p>
                                        <p
                                            v-if="form.updated_at && form.updated_at !== form.created_at"
                                            class="poppinsLight ppv2-text-black mb-0"
                                            :style="{ fontSize: normalizeFontSize(13) }"
                                        >
                                            {{ $t('product.modified_on') }} {{ formatDate(form.updated_at) }}
                                            {{ $t('product.by') }} {{ form.blogger }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="ppv2-text-black ppv2-rounded-[10px] ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-p-6"
                        >
                            <div class="ppv2-space-y-6">
                                <!-- Categories Section -->
                                <div class="ppv2-w-full">
                                    <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-2">
                                        <h2
                                            class="poppinsSemiBold ppv2-text-black mb-0"
                                            :style="{ fontSize: normalizeFontSize(18) }"
                                        >
                                            {{ $t('blog.titles.category') }}
                                        </h2>
                                        <button
                                            type="button"
                                            @click="showCategoryModal = true"
                                            class="ppv2-text-purple hover:ppv2-opacity-80"
                                        >
                                            <Plus :size="20" />
                                        </button>
                                    </div>
                                    <v-divider class="ppv2-m-0 ppv2-mb-4"></v-divider>
                                    <BaseSelect
                                        :label="$t('blog.select_categories')"
                                        v-model="form.categories"
                                        :options="availableCategories"
                                        optionLabel="name"
                                        optionValue="id"
                                        multiple
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    >
                                        <template #leftIcon>
                                            <Tag class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" :size="16" />
                                        </template>
                                    </BaseSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Bottom Actions -->
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="cancel" />
                            <BaseButton
                                variant="primary"
                                :loading="submitting"
                                @click="submit"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ $t('common.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>

        <!-- Category Modal -->
        <BlogCategoryModal v-model="showCategoryModal" :token="token" @category-created="onCategoryCreated" />
    </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import normalizeSize from '../../../../utils/normalizeSize'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BlogCategoryModal from './BlogCategoryModal.vue'

import { Plus, Trash2, ChevronUp, ChevronDown, Tag } from 'lucide-vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'BlogForm',
    components: {
        CustomInput,
        BackButton,
        BaseButton,
        BaseTextarea,
        BaseSelect,
        BaseSwitch,
        BaseDateInput,
        Plus,
        Trash2,
        ChevronUp,
        ChevronDown,
        CustomInputUpload,
        BlogCategoryModal,
        Tag,
    },
    props: {
        blogId: {
            type: [Number, String],
            default: null,
        },
        token: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            submitting: false,
            loading: false,
            form: {
                title: '',
                short_desc: '',
                body: '',
                categories: [],
                publish_at: moment().format('YYYY-MM-DD'),
                is_publish: true,
                seo: '',
                meta_desc: '',
                image: null,
                faqs: [],
                created_at: null,
                updated_at: null,
                blogger: null,
            },
            availableCategories: [],
            errors: {},
            imagePreview: null,
            showCategoryModal: false,
            originalImageUrl: null,
            removeImage: false,
        }
    },
    created() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatDate(date) {
            if (!date) return ''
            return moment(date).format('DD/MM/YYYY [à] HH[h]mm')
        },
        async fetchData() {
            this.loading = true
            try {
                const results = await Promise.all([
                    axios.get('/api/v3/blog/get-data', { headers: { Authorization: `Bearer ${this.token}` } }),
                    this.blogId
                        ? axios.get(`/api/v3/blog/${this.blogId}/edit`, { headers: { Authorization: `Bearer ${this.token}` } })
                        : Promise.resolve(null),
                ])

                this.availableCategories = results[0].data.categories

                if (results[1]) {
                    const blogData = results[1].data.data
                    this.form = {
                        ...this.form,
                        ...blogData,
                        is_publish: !!blogData.is_publish,
                        publish_at: blogData.publish_at ? moment(blogData.publish_at).format('YYYY-MM-DD') : this.form.publish_at,
                        categories: blogData.categories_ids || [], // Need to check if BlogResource returns categories_ids
                    }
                    // Handle image separately for preview if it's a string (URL)
                    if (typeof blogData.image === 'string') {
                        this.form.image = blogData.image
                        this.originalImageUrl = blogData.image
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        saveDraft() {
            this.form.is_publish = false
            this.submit()
        },
        publish() {
            this.form.is_publish = true
            this.submit()
        },
        triggerImageUpload() {
            this.$refs.imageInput.click()
        },
        onFileSelected(file) {
            if (!file) {
                this.form.image = null
                // If there was an original image URL and the user cleared it, flag for removal
                if (this.originalImageUrl) {
                    this.removeImage = true
                }
                return
            }

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
            if (!allowedTypes.includes(file.type)) {
                this.$set(this.errors, 'image', this.$t('experience.step2.errors.imageOnly'))
                this.form.image = null
                return
            }

            this.$delete(this.errors, 'image')
            this.removeImage = false
            this.form.image = file
        },
        cancel() {
            window.location.href = '/back-office/v3/blogs'
        },
        addFAQ() {
            this.form.faqs.push({ question: '', answer: '' })
        },
        removeFAQ(index) {
            this.form.faqs.splice(index, 1)
        },
        moveUp(index) {
            if (index > 0) {
                const item = this.form.faqs.splice(index, 1)[0]
                this.form.faqs.splice(index - 1, 0, item)
            }
        },
        moveDown(index) {
            if (index < this.form.faqs.length - 1) {
                const item = this.form.faqs.splice(index, 1)[0]
                this.form.faqs.splice(index + 1, 0, item)
            }
        },
        getFormData() {
            const formData = new FormData()
            Object.keys(this.form).forEach((key) => {
                if (key === 'faqs') {
                    this.form.faqs.forEach((faq, index) => {
                        formData.append(`faqs[${index}][question]`, faq.question)
                        formData.append(`faqs[${index}][answer]`, faq.answer)
                        formData.append(`faqs[${index}][order]`, index)
                    })
                } else if (key === 'image') {
                    // Only append if it's a new File object
                    if (this.form.image instanceof File) {
                        formData.append('image', this.form.image)
                    }
                } else if (Array.isArray(this.form[key])) {
                    this.form[key].forEach((val) => formData.append(`${key}[]`, val))
                } else if (this.form[key] !== null) {
                    formData.append(key, this.form[key])
                }
            })

            // Signal the backend to remove the image if the user cleared it
            if (this.removeImage) {
                formData.append('remove_image', '1')
            }

            return formData
        },
        onCategoryCreated(category) {
            this.availableCategories.push(category)
            if (!this.form.categories.includes(category.id)) {
                this.form.categories.push(category.id)
            }
        },
        async submit() {
            this.submitting = true
            this.errors = {}

            const formData = this.getFormData()

            try {
                const url = this.blogId ? `/api/v3/blog/${this.blogId}/update` : '/api/v3/blog/store'
                const response = await axios.post(url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${this.token}` },
                })
                if (response.status === 200) {
                    this.$toast.success(this.blogId ? this.$t('blog.update_success') : this.$t('blog.create_success'))
                    window.location.href = '/back-office/v3/blogs'
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                }
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.text-purple {
    color: #7d2dbd;
}
.title-header {
    font-weight: 500;
}
.upload-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
    opacity: 0.7;
}
.upload-container:hover,
.upload-container:focus,
.upload-container:active {
    opacity: 1;
}
</style>
